import React, { useState, useEffect, useCallback } from "react"
import QRCode from "react-qr-code"

// Fisher-Yates shuffle algorithm
function shuffleArray(array) {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// Build display list with game name, suggestions, and separators
function buildDisplayList(gameType, askfors) {
  const list = [{ type: 'game', content: gameType }]
  askfors.forEach((askfor, i) => {
    list.push({ type: 'suggestion', content: askfor })
    if (i < askfors.length - 1) {
      list.push({ type: 'separator', content: 'Next Suggestion...' })
    }
  })
  return list
}

export default function AudienceDisplay() {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Askfor navigation state
  const [displayList, setDisplayList] = useState([]) // Combined list: game + suggestions + separators
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loadedGameId, setLoadedGameId] = useState(null)

  // Games list for dropdown
  const [games, setGames] = useState([])

  // Poll for status updates every 3 seconds
  useEffect(() => {
    fetchStatus()
    fetchGames()
    const interval = setInterval(fetchStatus, 3000)
    return () => clearInterval(interval)
  }, [])

  // Fetch askfors when game changes
  useEffect(() => {
    if (status?.current_game?.id && status.current_game.id !== loadedGameId) {
      fetchAskfors()
    }
  }, [status?.current_game?.id, loadedGameId])

  // Keyboard event listener
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Only respond when in active half with a current game
      if (!status || !["First half", "Second half"].includes(status.status) || !status.current_game) {
        return
      }

      // Only navigate if game needs askfor and show_on_projector is enabled
      if (!status.current_game.needs_askfor || !status.current_game.show_on_projector) {
        return
      }

      // Check for backward navigation: Q or ArrowLeft
      if (event.key === "q" || event.key === "Q" || event.key === "ArrowLeft") {
        // Go backward
        setCurrentIndex(prev => Math.max(0, prev - 1))
      } else {
        // Any other key goes forward
        setCurrentIndex(prev => Math.min(displayList.length - 1, prev + 1))
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [status, displayList.length])

  const fetchStatus = async () => {
    try {
      const response = await fetch("/api/audience_display/status")
      if (response.ok) {
        const data = await response.json()

        // Reset display list if game changed
        if (status && data.current_game_id !== status.current_game_id) {
          setDisplayList([])
          setCurrentIndex(0)
          setLoadedGameId(null)
        }

        setStatus(data)
        setLoading(false)
        setError(null)
      } else {
        setError("No active show")
        setLoading(false)
      }
    } catch (error) {
      console.error("Error fetching status:", error)
      setError("Failed to connect to server")
      setLoading(false)
    }
  }

  const fetchAskfors = async () => {
    try {
      const response = await fetch("/api/audience_display/askfors")
      if (response.ok) {
        const data = await response.json()
        // Shuffle the askfors and build display list
        const shuffled = shuffleArray(data.askfors)
        const list = buildDisplayList(status?.current_game?.game_type || 'Game', shuffled)
        setDisplayList(list)
        setCurrentIndex(0) // Start at game title (index 0)
        setLoadedGameId(data.game_id)
      }
    } catch (error) {
      console.error("Error fetching askfors:", error)
    }
  }

  const fetchGames = async () => {
    try {
      const response = await fetch("/api/audience_display/games")
      if (response.ok) {
        const data = await response.json()
        setGames(data.games || [])
      }
    } catch (error) {
      console.error("Error fetching games:", error)
    }
  }

  const handleGameChange = async (event) => {
    const gameId = event.target.value
    if (!gameId) return

    try {
      const response = await fetch("/api/audience_display/set_current_game", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ game_id: gameId })
      })

      if (response.ok) {
        // Reset state and fetch new status
        setDisplayList([])
        setCurrentIndex(0)
        setLoadedGameId(null)
        fetchStatus()
      }
    } catch (error) {
      console.error("Error setting current game:", error)
    }
  }

  // Game selector dropdown component
  const GameSelector = () => (
    <div className="absolute top-4 left-4 z-10">
      <select
        value={status?.current_game_id || ""}
        onChange={handleGameChange}
        className="bg-white border border-gray-200 text-gray-700 rounded-lg shadow-sm px-3 py-2 cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        style={{ fontSize: '14px', minWidth: '200px' }}
      >
        <option value="">Select game...</option>
        {games.map((game, index) => (
          <option key={game.id} value={game.id}>
            {index + 1}. {game.game_type}
          </option>
        ))}
      </select>
    </div>
  )

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p style={{fontSize: '64px'}} className="text-black font-bold">Loading...</p>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-8">
        <div className="text-center">
          <h1 style={{fontSize: '64px'}} className="font-bold text-black mb-4">No Active Show</h1>
          <p style={{fontSize: '64px'}} className="text-black">{error}</p>
        </div>
      </div>
    )
  }

  // Pre-show or Intermission - Show QR Code
  if (status.status === "Waiting to begin" || status.status === "Intermission") {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8">
        <h1 style={{fontSize: '64px'}} className="font-bold text-black mb-12 text-center">
          To interact with our show, navigate here:
        </h1>
        <div className="bg-white p-8 mb-8">
          <QRCode
            value="https://www.phatask.com/app"
            size={400}
            level="M"
          />
        </div>
        <p style={{fontSize: '64px'}} className="font-semibold text-black">
          https://www.phatask.com/app
        </p>
      </div>
    )
  }

  // Active half (First half or Second half)
  if (status.status === "First half" || status.status === "Second half") {
    // No current game set
    if (!status.current_game) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center p-8 relative">
          <GameSelector />
          <div className="text-center">
            <h1 style={{fontSize: '64px'}} className="font-bold text-black mb-4">
              Waiting for game...
            </h1>
            <p style={{fontSize: '64px'}} className="text-black">Select a game from the dropdown</p>
          </div>
        </div>
      )
    }

    // No display list yet (still loading)
    if (displayList.length === 0) {
      const presenterScheme = status.color_schemes?.[status.current_game.presenter] || {}
      const bgStyle = { backgroundColor: presenterScheme.bg_color || '#ffffff', color: presenterScheme.text_color || '#000000' }
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-8 relative" style={bgStyle}>
          <GameSelector />
          <div className="text-center">
            <h1 style={{fontSize: '64px'}} className="font-bold text-center">
              {status.current_game.game_type}
            </h1>
            {status.current_game.live_askfor && (
              <p style={{fontSize: '32px'}} className="mt-6 opacity-80">
                {status.current_game.live_askfor}
              </p>
            )}
          </div>
          {status.current_game.needs_askfor && (
            <p style={{fontSize: '64px'}} className="mt-12">Loading suggestions...</p>
          )}
        </div>
      )
    }

    // Show current item from display list
    const currentItem = displayList[currentIndex]
    const presenterScheme = status.color_schemes?.[status.current_game.presenter] || {}
    const bgStyle = { backgroundColor: presenterScheme.bg_color || '#ffffff', color: presenterScheme.text_color || '#000000' }

    // Calculate suggestion count (excluding game title and separators)
    const totalSuggestions = displayList.filter(item => item.type === 'suggestion').length
    const currentSuggestionNumber = displayList
      .slice(0, currentIndex + 1)
      .filter(item => item.type === 'suggestion').length

    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-12 relative" style={bgStyle}>
        <GameSelector />
        <div className="p-12 max-w-5xl text-center">
          <h2 style={{fontSize: '64px'}} className="font-bold text-center break-words">
            {currentItem.content}
          </h2>
          {currentItem.type === 'game' && status.current_game.live_askfor && (
            <p style={{fontSize: '32px'}} className="mt-6 opacity-80">
              {status.current_game.live_askfor}
            </p>
          )}
        </div>
        {totalSuggestions > 0 && (
          <p style={{fontSize: '18px'}} className="text-gray-500 absolute bottom-4 right-4">
            {currentSuggestionNumber} of {totalSuggestions} suggestions
          </p>
        )}
      </div>
    )
  }

  // Complete status
  if (status.status === "Complete") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-8">
        <div className="text-center">
          <h1 style={{fontSize: '64px'}} className="font-bold text-black mb-4">
            Thank You!
          </h1>
          <p style={{fontSize: '64px'}} className="text-black">Show Complete</p>
        </div>
      </div>
    )
  }

  // Fallback
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      <div className="text-center">
        <h1 style={{fontSize: '64px'}} className="font-bold text-black mb-4">Unknown Status</h1>
        <p style={{fontSize: '64px'}} className="text-black">{status.status}</p>
      </div>
    </div>
  )
}
